import type { ReactNode } from 'react';

import { cn } from '@pesto/utils';

import type { ButtonProps } from './Button';

import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { LoadingSpinner } from './LoadingSpinner';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: ReactNode;
  description?: string;
  loading?: boolean;
  dismissible?: boolean;
  onConfirm?: () => void;
  confirmProps?: ButtonProps;
  confirmText?: string;
  className?: string;
  depth?: number;
  stackSize?: number;
}

const STACK_STEP_PX = 14;

const stackedStyle = (depth: number) => ({
  top: `calc(10vh - ${depth * STACK_STEP_PX}px)`,
  transform: `translate(-50%, 0) scale(${1 - depth * 0.03})`,
  transformOrigin: 'top center',
  maxHeight: '80vh',
});

export const Modal = (props: ModalProps) => {
  const {
    isOpen = false,
    title,
    description,
    loading = false,
    dismissible = true,
    onClose,
    className,
    onConfirm,
    children,
    confirmProps,
    depth = 0,
    stackSize = 1,
  } = props;

  const drawsBackdrop = depth === stackSize - 1;

  const Header =
    title || description ? (
      <DialogHeader className="mb-2">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
    ) : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && dismissible && onClose()}>
      <DialogContent
        className={cn('transition-[top,transform] duration-200', depth > 0 && 'overflow-hidden', className)}
        overlayClassName={drawsBackdrop ? undefined : 'bg-transparent backdrop-blur-none'}
        showCloseButton={dismissible}
        onEscapeKeyDown={dismissible ? undefined : (event) => event.preventDefault()}
        onPointerDownOutside={dismissible ? undefined : (event) => event.preventDefault()}
        style={stackSize > 1 ? stackedStyle(depth) : undefined}
      >
        {}
        {depth > 0 && (
          <div
            aria-hidden
            className="bg-background/50 pointer-events-none absolute -inset-4 z-50 backdrop-blur-[2px]"
          />
        )}
        {Header}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {children}
            {onConfirm && (
              <DialogFooter>
                <Button variant={'outline'} onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={onConfirm} {...confirmProps}>
                  Confirm
                </Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
